// Brevo adapter - frontend half.
//
// The browser never holds a Brevo API key. It POSTs to our own backend
// endpoint (see VITE_NEWSLETTER_ENDPOINT). The backend is the only thing
// that talks to Brevo. This file's job is purely the HTTP contract:
// shape the request, classify the response.

import type { EmailProvider, SubscribeInput, SubscribeResult } from './index'

// Require a real TLD (≥2 chars, no dots) so addresses like "s@s.s" are caught
// here instead of reaching Brevo, which rejects them with a confusing 502.
const RFC5322_LITE = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/

function endpoint(): string {
  /* v8 ignore start */
  const raw = (import.meta.env.VITE_NEWSLETTER_ENDPOINT as string | undefined) ?? ''
  /* v8 ignore stop */
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : '/api/newsletter/subscribe'
}

export const brevoProvider: EmailProvider = {
  async subscribe(input: SubscribeInput): Promise<SubscribeResult> {
    const email = input.email.trim()
    if (!RFC5322_LITE.test(email)) return { ok: false, reason: 'invalid_email' }

    let res: Response
    try {
      res = await fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          locale: input.locale,
          source: input.source,
          honeypot: input.honeypot ?? '',
          consentVersion: input.consentVersion ?? '1',
        }),
      })
    } catch {
      return { ok: false, reason: 'network' }
    }

    // 202 Accepted is the success path - the spec deliberately returns 202
    // even for already-subscribed emails to avoid leaking membership.
    if (res.status === 202 || res.status === 200) return { ok: true }
    if (res.status === 429) return { ok: false, reason: 'rate_limited' }
    if (res.status === 400) return { ok: false, reason: 'invalid_email' }
    return { ok: false, reason: 'server' }
  },
}
